import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, StatCard } from "@repo/o37-ui";
import UserHistoryModal from './UserHistoryModal';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type VDRLog = {
  id: string;
  visitor_email: string | null;
  visitor_name?: string | null;
  visitor_phone?: string | null;
  visitor_id: string;
  action_type: string;
  created_at: string;
  path: string;
  metadata: any;
  country?: string;
  city?: string;
  region?: string;
  device_info?: any;
  user_agent?: string;
};

type AggregatedUser = {
  id: string; // The grouping key (email or visitor_id)
  email: string | null;
  name: string | null;
  phone: string | null;
  visitorId: string;
  lastActive: string;
  firstSeen: string;
  visitCount: number;
  location: string;
  country?: string;
  totalTime: number; // estimated seconds
  devices: DeviceInfo[];
  logs: VDRLog[];
};

type DeviceInfo = {
  platform?: string;
  browser?: string;
  screenSize?: string;
  timezone?: string;
  userAgent?: string;
};

const getCountryFlag = (countryCodeOrName?: string) => {
  if (!countryCodeOrName) return '🌍';
  
  // 1. Try to generate flag from ISO 2-letter code
  if (countryCodeOrName.length === 2 && /^[A-Z]{2}$/i.test(countryCodeOrName)) {
    return countryCodeOrName
      .toUpperCase()
      .replace(/./g, char => String.fromCodePoint(char.charCodeAt(0) + 127397));
  }

  // 2. Fallback Map for Common Full Names
  const map: Record<string, string> = {
    'United States': '🇺🇸', 'USA': '🇺🇸', 'US': '🇺🇸',
    'United Kingdom': '🇬🇧', 'UK': '🇬🇧',
    'Germany': '🇩🇪', 'Deutschland': '🇩🇪',
    'France': '🇫🇷',
    'Canada': '🇨🇦',
    'Australia': '🇦🇺',
    'India': '🇮🇳',
    'Japan': '🇯🇵',
    'China': '🇨🇳',
    'Brazil': '🇧🇷',
    'Netherlands': '🇳🇱',
    'Sweden': '🇸🇪',
    'Switzerland': '🇨🇭',
    'Ireland': '🇮🇪',
    'Italy': '🇮🇹',
    'Spain': '🇪🇸',
    'Russia': '🇷🇺',
    'Singapore': '🇸🇬',
  };
  
  return map[countryCodeOrName] || '🌍';
};

export default function VDRTrackingTable({ roomId }: { roomId: string }) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AggregatedUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<AggregatedUser | null>(null);
  const [activeTab, setActiveTab] = useState('recent');
  const [error, setError] = useState<string | null>(null);

  const userRole = localStorage.getItem('app_role') || 'user';
  const userId = localStorage.getItem('app_user_id') || '';

  useEffect(() => {
    if (roomId) {
      loadLogs();
    }
  }, [roomId]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use API to bypass RLS issues on client
      const response = await fetch(`${API_URL}/vdr/${roomId}/logs`, {
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });

      if (!response.ok) {
         console.error("Error fetching logs");
         setError("Failed to load tracking data.");
         return;
      }

      const logs = await response.json() as VDRLog[];
      processLogs(logs);
      
    } catch (e) {
      console.error(e);
      setError("Error connecting to server.");
    } finally {
      setLoading(false);
    }
  };

  const processLogs = (logs: VDRLog[]) => {
    // 1. Group logs by visitor_id first to establish sessions
    const sessionMap = new Map<string, VDRLog[]>();
    const visitorEmailMap = new Map<string, string>();
    const visitorNameMap = new Map<string, string>();
    const visitorPhoneMap = new Map<string, string>();

    logs.forEach(log => {
      const vid = log.visitor_id || 'unknown';
      if (!sessionMap.has(vid)) {
        sessionMap.set(vid, []);
      }
      sessionMap.get(vid)!.push(log);

      // If we find identity info in this session, map the visitor_id to it
      if (log.visitor_email) {
        visitorEmailMap.set(vid, log.visitor_email);
      }
      if (log.visitor_name) {
        visitorNameMap.set(vid, log.visitor_name);
      }
      if (log.visitor_phone) {
        visitorPhoneMap.set(vid, log.visitor_phone);
      }
    });

    // 2. Aggregate into User Objects (merging cross-device sessions)
    const userMap = new Map<string, AggregatedUser>();

    sessionMap.forEach((sessionLogs, visitorId) => {
      // Determine the primary key: Email (if linked) OR VisitorID
      const linkedEmail = visitorEmailMap.get(visitorId);
      const linkedName = visitorNameMap.get(visitorId);
      const linkedPhone = visitorPhoneMap.get(visitorId);
      const key = linkedEmail || visitorId;

      if (!userMap.has(key)) {
        userMap.set(key, {
          id: key,
          email: linkedEmail || null,
          name: linkedName || null,
          phone: linkedPhone || null,
          visitorId: linkedEmail ? 'Multiple Devices' : visitorId,
          lastActive: sessionLogs[0].created_at,
          firstSeen: sessionLogs[0].created_at,
          visitCount: 0,
          location: 'Unknown',
          country: undefined,
          totalTime: 0,
          devices: [],
          logs: []
        });
      }

      const user = userMap.get(key)!;
      
      // Update name/phone if we found it later
      if (!user.name && linkedName) user.name = linkedName;
      if (!user.phone && linkedPhone) user.phone = linkedPhone;
      
      // Merge logs
      user.logs.push(...sessionLogs);
      
      // Track unique devices from this session
      const seenDevices = new Set<string>();
      
      // Update stats from these logs
      sessionLogs.forEach(log => {
        // Update Last Active (pick max)
        if (new Date(log.created_at) > new Date(user.lastActive)) {
            user.lastActive = log.created_at;
        }
        // Update First Seen (pick min)
        if (new Date(log.created_at) < new Date(user.firstSeen)) {
            user.firstSeen = log.created_at;
        }

        if (log.action_type === 'view_page') {
          user.visitCount++;
        }
        
        if (log.action_type === 'time_on_page') {
          // Fix: Convert ms to seconds
          const ms = Number(log.metadata?.duration_ms) || Number(log.metadata?.duration) || 0;
          user.totalTime += (ms / 1000);
        }

        // Enhance location
        if ((user.location === 'Unknown' || !user.location) && (log.city || log.country)) {
            user.location = [log.city, log.country].filter(Boolean).join(', ');
        }
        if (!user.country && log.country) {
            user.country = log.country;
        }
        
        // Extract device info
        if (log.device_info) {
          const di = log.device_info;
          const deviceKey = `${di.platform || 'Unknown'}-${di.screen_width || 0}x${di.screen_height || 0}`;
          if (!seenDevices.has(deviceKey)) {
            seenDevices.add(deviceKey);
            user.devices.push({
              platform: di.platform,
              browser: parseBrowser(di.user_agent || log.user_agent),
              screenSize: di.screen_width && di.screen_height ? `${di.screen_width}x${di.screen_height}` : undefined,
              timezone: di.timezone,
              userAgent: di.user_agent || log.user_agent
            });
          }
        }
      });
    });

    // Sort logs for each user by date desc
    userMap.forEach(user => {
        user.logs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    });

    setUsers(Array.from(userMap.values()));
  };
  
  // Helper to parse browser from user agent
  const parseBrowser = (ua?: string): string => {
    if (!ua) return 'Unknown';
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
    return 'Other';
  };

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getFilteredUsers = () => {
    let filtered = [...users];

    switch (activeTab) {
        case 'top':
            // Top Visitors: Sort by totalTime (or activity)
            return filtered.sort((a, b) => b.totalTime - a.totalTime);
        
        case 'known':
            // Known Visitors: Filter by email presence
            return filtered.filter(u => !!u.email).sort((a, b) => new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime());

        case 'active_anon':
            // Most active non-visitors (anonymous): Filter no email, sort by totalTime
            return filtered.filter(u => !u.email).sort((a, b) => b.totalTime - a.totalTime);

        case 'most_visited':
            // Most Visited Visitors: Sort by visitCount
            return filtered.sort((a, b) => b.visitCount - a.visitCount);

        case 'recent':
        default:
             // Most Recent: Sort by lastActive
            return filtered.sort((a, b) => new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime());
    }
  };

  // Calculate Stats
  const uniqueVisitors = users.length;
  const totalPageViews = users.reduce((acc, u) => acc + u.visitCount, 0);
  
  const totalTimeSeconds = users.reduce((acc, u) => acc + u.totalTime, 0);
  // Calculate average time ONLY for users who have recorded time (non-zero)
  const activeTimeUsers = users.filter(u => u.totalTime > 0).length;
  const avgTimeSeconds = activeTimeUsers > 0 ? Math.round(totalTimeSeconds / activeTimeUsers) : 0;
  
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    return `${m}m ${seconds % 60}s`;
  };

  const totalActions = users.reduce((acc, u) => {
    return acc + u.logs.length;
  }, 0);

  if (loading) {
    return (
        <Card>
            <CardContent className="p-8 text-center text-neutral-400">Loading tracking data...</CardContent>
        </Card>
    );
  }

  if (error) {
    return (
        <Card>
             <CardContent className="p-8 text-center text-red-500">{error}</CardContent>
        </Card>
    );
  }

  return (
    <>
        <Card>
        <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <CardTitle>Visitor Tracking</CardTitle>
                    <p className="text-sm text-neutral-500">Real-time activity logs and visitor history.</p>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard 
                    label="Unique Visitors" 
                    value={uniqueVisitors.toString()} 
                    hint="Total unique users" 
                />
                <StatCard 
                    label="Total Page Views" 
                    value={totalPageViews.toString()} 
                    hint="Across all sessions" 
                />
                <StatCard 
                    label="Avg. Time Spent" 
                    value={formatDuration(avgTimeSeconds)} 
                    hint="Active sessions only" 
                />
                <StatCard 
                    label="Total Actions" 
                    value={totalActions.toString()} 
                    hint="Clicks, views, downloads" 
                />
            </div>
            
            <div className="flex flex-wrap gap-2">
                {[
                    { id: 'recent', label: 'Recent' },
                    { id: 'top', label: 'Top Visitors' },
                    { id: 'known', label: 'Known Visitors' },
                    { id: 'active_anon', label: 'Top Anonymous' },
                    { id: 'most_visited', label: 'Most Frequent' },
                ].map(tab => (
                    <Button 
                        key={tab.id}
                        variant={activeTab === tab.id ? 'primary' : 'outline'}
                        onClick={() => setActiveTab(tab.id)}
                        className="text-xs h-8 px-3"
                        text={tab.label}
                    />
                ))}
            </div>
        </CardHeader>
        <CardContent className="p-0">
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-neutral-50 text-neutral-500 font-medium border-b border-neutral-100">
                        <tr>
                            <th className="px-6 py-3 w-12"></th>
                            <th className="px-6 py-3 w-1/3">User / Visitor</th>
                            <th className="px-6 py-3">Location</th>
                            <th className="px-6 py-3 text-center">Visits</th>
                            <th className="px-6 py-3 text-right">Last Active</th>
                            <th className="px-6 py-3 w-24"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {getFilteredUsers().length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-8 text-center text-neutral-400">
                                    {users.length === 0 ? "No tracking data available yet." : "No visitors found for this filter."}
                                </td>
                            </tr>
                        ) : (
                            getFilteredUsers().map((user) => (
                                <tr key={user.id} className="hover:bg-neutral-50/50 transition-colors">
                                    <td className="pl-6 py-4 text-2xl">
                                        {getCountryFlag(user.country)}
                                    </td>
                                    <td className="px-6 py-4">
                                        {user.email ? (
                                            <div>
                                                <div className="font-medium text-neutral-900">{user.email}</div>
                                                <div className="text-xs text-neutral-400 font-mono mt-0.5 max-w-[150px] truncate" title={user.visitorId}>{user.visitorId}</div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="font-medium text-neutral-600 italic">Anonymous Visitor</div>
                                                <div className="text-xs text-neutral-400 font-mono mt-0.5">{user.visitorId.substring(0, 12)}...</div>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-neutral-600">
                                        {user.location || "Unknown"}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800">
                                            {user.visitCount}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right text-neutral-500">
                                        {formatTimeAgo(user.lastActive)}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <Button 
                                            variant="outline" 
                                            onClick={() => setSelectedUser(user)}
                                            text="View"
                                        />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </CardContent>
        </Card>

        {selectedUser && (
            <UserHistoryModal 
                user={selectedUser} 
                logs={selectedUser.logs} 
                onClose={() => setSelectedUser(null)} 
            />
        )}
    </>
  );
}

